<template>
  <v-form ref="AUTHORIZED_CREDIT" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="AUTHORIZED_CREDIT">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <etablissement-select v-if="isDisplayed('ETABLISSEMENT_ID')"
                              ref="ETABLISSEMENT_ID"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=etablissementId
                              :moduleId=moduleId
                              libelle="'Établissement'"
                              newLabel=''
                              selectText=""
                              @status=statusEtablissementId
                              :requiredAttributs="requiredAttributs"
                              :autoSelect=true
                              :ressource=ressourceEtablissementSelect
                              :filters=etablissementIdFilter
                              :toggleReset=resetEtablissementId
                              :required="isRequired('ETABLISSEMENT_ID')"
                              :contextId=authorizedCredit.id
                              :readOnly="readOnly('ETABLISSEMENT_ID')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-select>
        <authorized-credit-credit-limit v-if="isDisplayed('CREDIT_LIMIT')"
                                        ref="CREDIT_LIMIT"
                                        :theme="theme"
                                        :showMissing="showMissing"
                                        v-model:value=creditLimit
                                        @status=statusCreditLimit
                                        :required="isRequired('CREDIT_LIMIT')"
                                        :contextId=authorizedCredit.id
                                        :readOnly="readOnly('CREDIT_LIMIT')"
                                        :objType=objType
                                        :fiche=fiche
                                        :sectionName="sectionName">
        </authorized-credit-credit-limit>
        <authorized-credit-permanent v-if="isDisplayed('PERMANENT')"
                                     ref="PERMANENT"
                                     :theme="theme"
                                     :showMissing="showMissing"
                                     v-model:value=permanent
                                     @status=statusPermanent
                                     :required="isRequired('PERMANENT')"
                                     :contextId=authorizedCredit.id
                                     :readOnly="readOnly('PERMANENT')"
                                     :objType=objType
                                     :fiche=fiche
                                     :sectionName="sectionName">
        </authorized-credit-permanent>
        <authorized-credit-credit-limit-until v-if="isDisplayed('CREDIT_LIMIT_UNTIL')"
                                              ref="CREDIT_LIMIT_UNTIL"
                                              :theme="theme"
                                              :showMissing="showMissing"
                                              v-model:value=creditLimitUntil
                                              @status=statusCreditLimitUntil
                                              :periods='periodCreditLimitUntil'
                                              :required="isRequired('CREDIT_LIMIT_UNTIL')"
                                              :contextId=authorizedCredit.id
                                              :readOnly="readOnly('CREDIT_LIMIT_UNTIL')"
                                              :objType=objType
                                              :fiche=fiche
                                              :sectionName="sectionName">
        </authorized-credit-credit-limit-until>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudAuthorizedCredit from '@/generated/cruds/CrudAuthorizedCredit';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudAuthorizedCredit,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.AUTHORIZED_CREDIT,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceEtablissementSelect () { return RessourceGenControl(GenControl.AUTHORIZED_CREDIT_ETABLISSEMENT_SELECT); },

      ressource () { return RessourceGenSection(GenSection.AUTHORIZED_CREDIT); },

      fields (): string[] {
        return ['ETABLISSEMENT_ID', 'CREDIT_LIMIT', 'PERMANENT', 'CREDIT_LIMIT_UNTIL'];
      },

      extraButtons (): Button[] {
        return [];
      },

      etablissementIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("ETABLISSEMENT_ID", false);
          this.readOnlyMap.set("CREDIT_LIMIT", false);
          this.readOnlyMap.set("PERMANENT", false);
          this.readOnlyMap.set("CREDIT_LIMIT_UNTIL", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("ETABLISSEMENT_ID", true);
          this.displayMap.set("CREDIT_LIMIT", true);
          this.displayMap.set("PERMANENT", this.requiredMissing(requiredAttributs, "PERMANENT") || !!this.fiche);
          this.displayMap.set("CREDIT_LIMIT_UNTIL", this.requiredMissing(requiredAttributs, "CREDIT_LIMIT_UNTIL") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("ETABLISSEMENT_ID", true);
          this.requiredMap.set("CREDIT_LIMIT", true);
          this.requiredMap.set("PERMANENT", this.requiredMissing(requiredAttributs, "PERMANENT") || !this.fiche);
          this.requiredMap.set("CREDIT_LIMIT_UNTIL", this.requiredMissing(requiredAttributs, "CREDIT_LIMIT_UNTIL") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoCredit' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
